import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useActions } from '@hooks/useActions';
import './Folders.scss';
import Button from '@atoms/Button';
import Tree from '@atoms/Tree';
import {
    MdAdd,
    MdOpenInNew,
    MdOutlineDescription,
    MdOutlineHome,
    MdOutlineInsertChart,
    MdSettings,
    MdWorkOutline,
} from 'react-icons/md';
import { IListElement, ITreeOption } from '@/types';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { DocumentActionsService } from '@services/actions/DocumentActionsService';
import { LocationTypes } from '@models/actions/LocationTypes';
import { IBaseAction } from '@models/actions/IBaseAction';
import { DocActionsExecFactory } from '@organisms/actions/DocActionsExecFactory';
import Menu from '@atoms/Menu';
import { NoActionsItem } from '@models/actions/ActionConstants';
import PlanningMenuActionsControl from '@controls/PlanningMenuActionsControl';
import { FoldersService } from '@services/FoldersService';
import logo from '@images/logo.svg';
import { ReactSVG } from 'react-svg';
import clsx from 'clsx';
import { MdDictionary } from '@icons/react-icons-ext';
import { useAnalytics } from '@/analytics';
import Tooltip from '@atoms/Tooltip';

export interface IFoldersProps {
    /** сворачивание */
    isOpen: boolean;
}

const iconExtensions = ['png', 'svg', 'jpg'];

const getIcon = (icon: any) => {
    let parts = icon.split('.');
    let path = logo;

    if (parts.length > 1 && iconExtensions.some((x) => x.trim().toLowerCase() == parts[1].trim().toLowerCase())) {
        let logoName = path.substring(path.indexOf('logo'));
        path = path.replace(logoName, icon);
        if (parts[1].trim().toLowerCase() === 'svg') {
            return <ReactSVG src={path} />;
        } else {
            return <img src={path} alt="icon" />;
        }
    } else {
        switch (icon.trim().toLowerCase()) {
            case 'home':
                return <MdOutlineHome size="24" />;
            case 'tasks':
                return <MdWorkOutline size="24" />;
            case 'docs':
                return <MdOutlineDescription size="24" />;
            case 'dicts':
                return <MdDictionary size="24" />;
            case 'reports':
                return <MdOutlineInsertChart size="24" />;
            default:
                return <></>;
        }
    }
};

const lazyLoadItems = async (o: ITreeOption) => {
    let result = [] as ITreeOption[];
    if (o.hasChildren && (o.children == undefined || o.children.length == 0) && o.value) {
        let parts = o.value.split('/');
        let response: any;
        switch (parts[0].toLowerCase().trim()) {
            case 'tasks':
                response = await FoldersService.tasks(parts[1]);
                break;
            case 'documents':
                response = await FoldersService.documents(parts[1]);
                break;
            case 'dictionaries':
                response = await FoldersService.dictionaries(parts[1]);
                break;
            case 'reports':
                response = await FoldersService.reports(parts[1]);
        }
        result = response.data;
    }
    return result;
};

const Folders: FC<IFoldersProps> = ({ isOpen = true }: IFoldersProps) => {
    const { analytics } = useAnalytics();
    const user = useTypedSelector((state) => state.auth.user);
    const isMenuOpen = useTypedSelector((state) => state.mainLayout.isMenuOpen);
    const isAuth = useTypedSelector((state) => state.auth.isAuth);
    const { toggleMenu } = useActions();
    const [isCreateDocActionsLoading, setIsCreateDocActionsLoading] = useState<boolean>();
    const [createDocActions, setCreateDocActions] = useState<IListElement[]>([]);
    const [element, setElement] = useState<ReactElement>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [menuBarActions, setMenuBarActions] = useState<IBaseAction[]>([]);
    const [list, setList] = useState<ITreeOption[]>([]);

    const location = useLocation();
    const navigate = useNavigate();

    const searchType = searchParams.get('searchType');

    useEffect(() => {
        FoldersService.root().then((res) => {
            const data: ITreeOption[] = [...res.data];
            data.forEach((item) => {
                item.icon = item.icon && getIcon(item.icon);
            });
            if (user.canView.adminPanel) {
                data.push({
                    value: 'administrations',
                    label: 'Администрирование',
                    hasChildren: true,
                    checked: false,
                    icon: <MdSettings size="24" />,
                    loading: false,
                    children: [
                        {
                            value: 'addressBook',
                            label: 'Условные группы',
                            checked: false,
                            hasChildren: false,
                            url: '/administrations/dyngroups',
                        },
                    ],
                    url: '/administrations',
                });
            }

            setList(data);
        });
    }, []);

    // -------------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        DocumentActionsService.fetchActionsByLocation(undefined, LocationTypes.PlanningMenuBar, searchType).then(
            (res) => {
                setMenuBarActions(res.data);
            },
        );
    }, [searchType]);

    // -------------------------------------------------------------------------------------------------------------------

    const onCreateDocActionClick = useCallback(
        (act: IBaseAction) => {
            let executor = DocActionsExecFactory.GetActionExecutor(act);
            if (!executor) throw new Error(`Не обнаружен executor для действия ${act.type}`);
            executor.navigate = navigate;
            executor.location = location;
            executor.run(
                '',
                undefined,
                act,
                undefined,
                (succeed) => {
                    setElement(undefined);
                },
                () => {
                    setElement(executor?.visualElement && executor?.visualElement());
                },
            );
            setElement(executor?.visualElement && executor?.visualElement());

            analytics.trackEvent({
                category: 'Document',
                action: `Клик по кнопке '${act.displayName}' из меню Создать документ на боковой панели`,
                name: `sidebar-create-doc-menu--${act.key}`,
            });
        },
        [analytics, location, navigate],
    );

    const onCreateDocActionsOpening = useCallback(() => {
        if (!isCreateDocActionsLoading) {
            setIsCreateDocActionsLoading(true);

            DocumentActionsService.fetchActionsByLocation(undefined, LocationTypes.PlanningMenu, searchType)
                .then((res) => {
                    let actionItems = res.data.map((act: IBaseAction, i: number) => {
                        return {
                            label: act.displayName,
                            value: i.toString(),
                            handler: () => onCreateDocActionClick(act),
                            endAdornment: act.options?.showOpenInNewTabButton
                                ? () => (
                                      <div className="doc-button--new-tab">
                                          <Tooltip position="bottom">
                                              <Button
                                                  buttonType="icon"
                                                  size="xxs"
                                                  aria-label="Открыть в новой вкладке"
                                                  onClick={() => {
                                                      const newAction = { ...act };
                                                      const options = newAction.options ? { ...newAction.options } : {};
                                                      options.newTab = true;
                                                      newAction.options = options;
                                                      onCreateDocActionClick(newAction);
                                                  }}
                                                  startAdornment={<MdOpenInNew size="20" />}
                                              />
                                              Открыть в новой вкладке
                                          </Tooltip>
                                      </div>
                                  )
                                : undefined,
                        } as IListElement;
                    });
                    if (actionItems.length === 0) actionItems = [NoActionsItem];
                    setCreateDocActions(actionItems);
                })
                .finally(() => {
                    setIsCreateDocActionsLoading(false);
                });
        }

        analytics.trackEvent({
            category: 'Document',
            action: 'Клик по кнопке Создать документ',
            name: 'sidebar-create-doc',
        });
    }, [isCreateDocActionsLoading, onCreateDocActionClick, searchType, analytics]);

    const onClickTree = (e: ITreeOption) => {
        if (!isMenuOpen) toggleMenu(true);
    };

    return isAuth ? (
        <div
            className={clsx(
                'rf-folders',
                isOpen ? 'rf-folders--open' : 'rf-folders--close',
                isCreateDocActionsLoading && 'rf-folders--loading',
            )}
        >
            {element}
            <Menu
                list={createDocActions}
                position="bottom-start"
                offsetValue={{ mainAxis: -4, crossAxis: 12 }}
                toggleTarget={false}
                className="create-doc-menu"
            >
                <Button
                    size="s"
                    title="Создать документ"
                    startAdornment={<MdAdd className="rotate-on-hover" size="20" />}
                    className={clsx('doc-button', 'button-margin', 'full-width-button')}
                    data-testid="create-doc-button"
                    onClick={onCreateDocActionsOpening}
                >
                    Создать документ
                </Button>
            </Menu>
            <PlanningMenuActionsControl actions={menuBarActions} />
            <Tree lazyLoad={lazyLoadItems} id="folders" open={false} list={list} onClick={onClickTree} />
        </div>
    ) : (
        <></>
    );
};

export default Folders;
